import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminRegionDto, ReopenedRunDto } from '@rw/shared';
import { useState } from 'react';
import { Sheet } from '../../components/Sheet';
import { LoadingState } from '../../components/states';
import { Button, Card } from '../../components/ui';
import { invalidateCompetitive } from '../../hooks/queries';
import { ApiError, api } from '../../lib/api';
import { formatKm, formatNumber } from '../../lib/format';
import { toast } from '../../stores/toasts';
import { t as tr, useT } from '../../i18n';

/** Game owner: the cities, and opening a city (tested by the owner first) to every runner. */
export function CitiesTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'regions'], queryFn: () => api<AdminRegionDto[]>('/admin/regions') });
  const [opening, setOpening] = useState<AdminRegionDto | null>(null);
  const t = useT();
  const close = useMutation({
    mutationFn: (id: string) => api<AdminRegionDto[]>(`/admin/regions/${id}`, { method: 'PATCH', body: { isPublic: false } }),
    onSuccess: (rows) => {
      qc.setQueryData(['admin', 'regions'], rows);
      void qc.invalidateQueries({ queryKey: ['regions'] });
      toast({ kind: 'success', title: tr('City closed: only you see it now') });
    },
  });
  if (!q.data) return <LoadingState />;
  return (
    <div className="mt-4 space-y-3" data-testid="admin-cities">
      <p className="text-sm text-ink-400">{t('A new city is seen and played by you only until you open it to every runner.')}</p>
      {q.data.map((r) => (
        <Card key={r.id} className="flex items-center justify-between gap-3" data-testid={`admin-city-${r.id}`}>
          <div className="min-w-0">
            <div className="font-display text-xl font-bold">{r.name}</div>
            <div className="text-xs text-ink-400">
              {t('{sectors} sectors · {runs} valid runs', { sectors: formatNumber(r.territoryCount), runs: formatNumber(r.validRuns) })} ·{' '}
              <span className={r.isPublic ? 'text-volt' : 'text-gold'}>{t(r.isPublic ? 'Open to everyone' : 'Owner only')}</span>
            </div>
          </div>
          {r.isPublic ? (
            <Button size="sm" variant="secondary" loading={close.isPending && close.variables === r.id} onClick={() => close.mutate(r.id)} data-testid={`admin-city-close-${r.id}`}>
              {t('Close')}
            </Button>
          ) : (
            <Button size="sm" onClick={() => setOpening(r)} data-testid={`admin-city-open-${r.id}`}>
              {t('Open')}
            </Button>
          )}
        </Card>
      ))}
      <OpenCitySheet region={opening} onClose={() => setOpening(null)} />
    </div>
  );
}

function OpenCitySheet({ region, onClose }: { region: AdminRegionDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const t = useT();
  // Runs rejected earlier as "outside the game area" that lie in this city: judged again once it opens.
  const preview = useQuery({
    queryKey: ['admin', 'regions', region?.id, 'reopen'],
    queryFn: () => api<ReopenedRunDto[]>(`/admin/regions/${region!.id}/reopen`),
    enabled: !!region,
  });
  const open = useMutation({
    mutationFn: async (id: string) => {
      const rows = await api<AdminRegionDto[]>(`/admin/regions/${id}`, { method: 'PATCH', body: { isPublic: true } });
      const runs = preview.data?.length ? await api<ReopenedRunDto[]>(`/admin/regions/${id}/reopen`, { method: 'POST' }) : [];
      return { rows, runs };
    },
    onSuccess: ({ rows, runs }) => {
      qc.setQueryData(['admin', 'regions'], rows);
      void qc.invalidateQueries({ queryKey: ['regions'] });
      invalidateCompetitive(qc);
      toast({ kind: 'success', title: tr('{city} is open to everyone', { city: region?.name ?? '' }), body: runs.length ? tr('{n} earlier runs now count there', { n: runs.length }) : undefined });
      onClose();
    },
    onError: (e) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : tr('Could not open the city') }),
  });
  return (
    <Sheet open={!!region} onClose={onClose} label={t('Open city')}>
      {region ? (
        <div data-testid="admin-open-city-sheet">
          <h2 className="font-display text-2xl font-bold">{t('Open {city} to everyone?', { city: region.name })}</h2>
          <p className="mt-2 text-sm text-ink-300">
            {t('Every runner can then see its map, run there, and pick it for events. Runs there count for XP, distance, sectors and rankings.')}
          </p>
          {preview.isPending ? (
            <LoadingState />
          ) : preview.data?.length ? (
            <div className="mt-3 rounded-xl bg-ink-900 p-3 text-sm" data-testid="admin-reopen-preview">
              <div className="font-semibold">
                {t('{n} earlier runs there were rejected as outside the game area. They will be judged again and count:', { n: preview.data.length })}
              </div>
              <ul className="mt-2 space-y-1 text-ink-300">
                {preview.data.map((r) => (
                  <li key={r.runId}>
                    {r.user} · {formatKm(r.distanceM, 2)} km
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="mt-3 text-sm text-ink-400" data-testid="admin-reopen-none">{t('No earlier runs to judge again.')}</p>
          )}
          <div className="mt-4 flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button className="flex-1" disabled={preview.isPending} loading={open.isPending} onClick={() => open.mutate(region.id)} data-testid="admin-open-city-submit">
              {t('Open city')}
            </Button>
          </div>
        </div>
      ) : null}
    </Sheet>
  );
}
